/**
 * storageService — single source of truth for browser storage.
 *
 * Rules:
 *  - All keys live here as constants (no magic strings elsewhere).
 *  - sessionStorage → data that expires when the tab closes (user session).
 *  - localStorage   → data that persists across sessions (preferences, profile).
 *  - Never throw; return null / default values on missing or malformed data.
 */

// ── Key registry ──────────────────────────────────────────────────────────────

const KEYS = {
  // sessionStorage — session-scoped
  COMPANY_ID:       "companyId",
  USER_ID:          "userId",
  PLAN_ID:          "planId",
  CORPORATION_ID:   "corporationId",
  SELECTED_CORPORATE: "selectedCorporate",
  COMPANY_DUE_DATE: "companyDueDate",
  VARIABLES:        "variables",
  LANGUAGE:         "i18nextLng",
  VOLUME:           "volume",
  AFFILIATE_REF:    "affiliateRef",
  CSHOW:            "cshow",

  // localStorage — persistent preferences
  THEME:                    "melizai-theme",
  ACTIVE_BUSINESS:          "melizai-active-business",
  USER_PROFILE_PHOTO:       "melizai-user-profile-photo",
  CORPORATE_PROFILE_PHOTO:  "melizai-corporate-profile-photo",
  IAI_VOICE:                "melizai-iai-voice",
  IAI_VOICE_RATE:           "melizai-iai-voice-rate",
  IAI_VOICE_PITCH:          "melizai-iai-voice-pitch",
  IAI_AUTO_SPEAK:           "melizai-iai-auto-speak",
  CRM_KANBAN_ORDER_BY:      "crmKanbanOrderBy",
} as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Quota exceeded or private mode — fail silently
  }
}

function safeRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {}
}

function parseNumber(value: string | null): number | null {
  if (value === null) return null;
  const n = Number(value);
  return isNaN(n) ? null : n;
}

function parseJSON<T>(value: string | null): T | null {
  if (value === null) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

// ── Session storage (tab-scoped) ──────────────────────────────────────────────

export const session = {
  // companyId
  getCompanyId: (): number | null =>
    parseNumber(safeGet(sessionStorage, KEYS.COMPANY_ID)),
  setCompanyId: (id: number | string): void =>
    safeSet(sessionStorage, KEYS.COMPANY_ID, String(id)),
  removeCompanyId: (): void =>
    safeRemove(sessionStorage, KEYS.COMPANY_ID),

  // userId
  getUserId: (): number | null =>
    parseNumber(safeGet(sessionStorage, KEYS.USER_ID)),
  setUserId: (id: number | string): void =>
    safeSet(sessionStorage, KEYS.USER_ID, String(id)),
  removeUserId: (): void =>
    safeRemove(sessionStorage, KEYS.USER_ID),

  // planId
  getPlanId: (): number | null =>
    parseNumber(safeGet(sessionStorage, KEYS.PLAN_ID)),
  setPlanId: (id: number | string): void =>
    safeSet(sessionStorage, KEYS.PLAN_ID, String(id)),
  removePlanId: (): void =>
    safeRemove(sessionStorage, KEYS.PLAN_ID),

  // corporationId
  getCorporationId: (): number | null =>
    parseNumber(safeGet(sessionStorage, KEYS.CORPORATION_ID)),
  setCorporationId: (id: number | string): void =>
    safeSet(sessionStorage, KEYS.CORPORATION_ID, String(id)),
  removeCorporationId: (): void =>
    safeRemove(sessionStorage, KEYS.CORPORATION_ID),

  // selectedCorporate (JSON object)
  getSelectedCorporate: <T = unknown>(): T | null =>
    parseJSON<T>(safeGet(sessionStorage, KEYS.SELECTED_CORPORATE)),
  setSelectedCorporate: (value: unknown): void =>
    safeSet(sessionStorage, KEYS.SELECTED_CORPORATE, JSON.stringify(value)),
  removeSelectedCorporate: (): void =>
    safeRemove(sessionStorage, KEYS.SELECTED_CORPORATE),

  // companyDueDate
  getCompanyDueDate: (): string | null =>
    safeGet(sessionStorage, KEYS.COMPANY_DUE_DATE),
  setCompanyDueDate: (date: string): void =>
    safeSet(sessionStorage, KEYS.COMPANY_DUE_DATE, date),

  // variables (JSON)
  getVariables: <T = unknown>(): T | null =>
    parseJSON<T>(safeGet(sessionStorage, KEYS.VARIABLES)),
  setVariables: (value: unknown): void =>
    safeSet(sessionStorage, KEYS.VARIABLES, JSON.stringify(value)),

  // language
  getLanguage: (): string =>
    safeGet(sessionStorage, KEYS.LANGUAGE) ?? "es",
  setLanguage: (lang: string): void =>
    safeSet(sessionStorage, KEYS.LANGUAGE, lang),

  // volume
  getVolume: (): number =>
    parseNumber(safeGet(sessionStorage, KEYS.VOLUME)) ?? 1,
  setVolume: (v: number): void =>
    safeSet(sessionStorage, KEYS.VOLUME, String(v)),

  // affiliateRef
  getAffiliateRef: (): string | null =>
    safeGet(sessionStorage, KEYS.AFFILIATE_REF),
  setAffiliateRef: (ref: string): void =>
    safeSet(sessionStorage, KEYS.AFFILIATE_REF, ref),

  // Clear all session data (call on logout)
  clearAll: (): void => {
    [
      KEYS.COMPANY_ID,
      KEYS.USER_ID,
      KEYS.PLAN_ID,
      KEYS.CORPORATION_ID,
      KEYS.SELECTED_CORPORATE,
      KEYS.COMPANY_DUE_DATE,
      KEYS.VARIABLES,
      KEYS.AFFILIATE_REF,
      KEYS.CSHOW,
    ].forEach((key) => safeRemove(sessionStorage, key));
  },
};

// ── Local storage (persistent) ────────────────────────────────────────────────

export const local = {
  // Theme
  getTheme: (): "light" | "dark" =>
    (safeGet(localStorage, KEYS.THEME) as "light" | "dark") ?? "light",
  setTheme: (theme: "light" | "dark"): void =>
    safeSet(localStorage, KEYS.THEME, theme),

  // Active business (JSON)
  getActiveBusiness: <T = unknown>(): T | null =>
    parseJSON<T>(safeGet(localStorage, KEYS.ACTIVE_BUSINESS)),
  setActiveBusiness: (value: unknown): void =>
    safeSet(localStorage, KEYS.ACTIVE_BUSINESS, JSON.stringify(value)),
  removeActiveBusiness: (): void =>
    safeRemove(localStorage, KEYS.ACTIVE_BUSINESS),

  // Profile photos
  getUserProfilePhoto: (): string | null =>
    safeGet(localStorage, KEYS.USER_PROFILE_PHOTO),
  setUserProfilePhoto: (data: string): void =>
    safeSet(localStorage, KEYS.USER_PROFILE_PHOTO, data),

  getCorporateProfilePhoto: (): string | null =>
    safeGet(localStorage, KEYS.CORPORATE_PROFILE_PHOTO),
  setCorporateProfilePhoto: (data: string): void =>
    safeSet(localStorage, KEYS.CORPORATE_PROFILE_PHOTO, data),

  // IAI voice settings
  getIaiVoice: (): string | null =>
    safeGet(localStorage, KEYS.IAI_VOICE),
  setIaiVoice: (voice: string): void =>
    safeSet(localStorage, KEYS.IAI_VOICE, voice),

  getIaiVoiceRate: (): number =>
    parseNumber(safeGet(localStorage, KEYS.IAI_VOICE_RATE)) ?? 1,
  setIaiVoiceRate: (rate: number): void =>
    safeSet(localStorage, KEYS.IAI_VOICE_RATE, String(rate)),

  getIaiVoicePitch: (): number =>
    parseNumber(safeGet(localStorage, KEYS.IAI_VOICE_PITCH)) ?? 1,
  setIaiVoicePitch: (pitch: number): void =>
    safeSet(localStorage, KEYS.IAI_VOICE_PITCH, String(pitch)),

  getIaiAutoSpeak: (): boolean =>
    safeGet(localStorage, KEYS.IAI_AUTO_SPEAK) === "true",
  setIaiAutoSpeak: (value: boolean): void =>
    safeSet(localStorage, KEYS.IAI_AUTO_SPEAK, String(value)),

  // CRM kanban
  getCrmKanbanOrderBy: (): string | null =>
    safeGet(localStorage, KEYS.CRM_KANBAN_ORDER_BY),
  setCrmKanbanOrderBy: (value: string): void =>
    safeSet(localStorage, KEYS.CRM_KANBAN_ORDER_BY, value),

  // Per-company LLM API key (dynamic key)
  getLlmApiKey: (companyId: string | number): string | null =>
    safeGet(localStorage, `melizai-llm-api-${companyId}`),
  setLlmApiKey: (companyId: string | number, key: string): void =>
    safeSet(localStorage, `melizai-llm-api-${companyId}`, key),
  removeLlmApiKey: (companyId: string | number): void =>
    safeRemove(localStorage, `melizai-llm-api-${companyId}`),
};

// ── Convenience re-export ─────────────────────────────────────────────────────

export const storageService = { session, local, KEYS };
export default storageService;
